import escapeRegExp from 'lodash/escapeRegExp';

const PR_DESCRIPTION_BUTTON_IMAGES_URL = 'https://www.cubic.dev/buttons';

/**
 * One of cubic's image buttons in the AI section: a `public/buttons` image pair
 * that follows the reader's color scheme. Every button is rendered here so the
 * patterns below that recognise them stay in step with their markup.
 */
export function renderPrDescriptionButton(button: { href: string; alt: string; image: string }) {
  const dark = `${PR_DESCRIPTION_BUTTON_IMAGES_URL}/${button.image}-dark.svg`;
  const light = `${PR_DESCRIPTION_BUTTON_IMAGES_URL}/${button.image}-light.svg`;
  return [
    `<a href="${button.href}" target="_blank" rel="noopener noreferrer" data-no-image-dialog="true">`,
    '<picture>',
    `<source media="(prefers-color-scheme: dark)" srcset="${dark}">`,
    `<source media="(prefers-color-scheme: light)" srcset="${light}">`,
    // Clients that ignore the sources, such as many email apps, show this on white, where the
    // dark variant of the main button is near-white.
    `<img alt="${button.alt}" src="${light}">`,
    '</picture>',
    '</a>',
  ].join('');
}

const CUBIC_PR_REVIEW_HREF_PATTERN =
  'https:\\/\\/(?:www\\.)?cubic\\.dev\\/pr\\/[^"\'>\\s]*\\/pull\\/[^"\'>\\s]*';
const REVIEW_IN_CUBIC_LINK_CONTENT_PATTERN =
  '(?:<picture>[\\s\\S]*?review-in-cubic-(?:dark|light)\\.svg[\\s\\S]*?<\\/picture>|Review in cubic)';
const GENERATED_REVIEW_IN_CUBIC_BUTTON_PATTERN = [
  `<a\\s[^>]*href=["']${CUBIC_PR_REVIEW_HREF_PATTERN}["'][^>]*data-no-image-dialog=["']true["'][^>]*>`,
  '\\s*<picture>[\\s\\S]*?review-in-cubic-(?:dark|light)\\.svg[\\s\\S]*?<\\/picture>\\s*',
  '<\\/a>',
  '\\s*',
].join('');
const COMMIT_SHA_PATTERN = '[a-f0-9]+';
const GENERATED_PR_DESCRIPTION_FOOTER_PREFIX_PATTERN = '<su[bp]>\\s*Written for commit ';
const GENERATED_PR_DESCRIPTION_FOOTER_SUFFIX_PATTERN =
  '\\.\\s*Summary will update (?:automatically )?on new commits\\.';
// Narrowly matches the commit-attribution line so callers can compare the
// generated summary's commit without stripping or parsing the adjacent CTA link.
const GENERATED_PR_DESCRIPTION_FOOTER_COMMIT_SHA_REGEX = new RegExp(
  `${GENERATED_PR_DESCRIPTION_FOOTER_PREFIX_PATTERN}(${COMMIT_SHA_PATTERN})${GENERATED_PR_DESCRIPTION_FOOTER_SUFFIX_PATTERN}`,
  'i',
);

/**
 * Matches generated commit attribution footers and their adjacent GitHub-to-cubic CTA.
 * Keep this client-safe so portal rendering can hide the CTA without changing stored PR bodies.
 */
export const GENERATED_PR_DESCRIPTION_FOOTER_REGEX = new RegExp(
  [
    `${GENERATED_PR_DESCRIPTION_FOOTER_PREFIX_PATTERN}${COMMIT_SHA_PATTERN}${GENERATED_PR_DESCRIPTION_FOOTER_SUFFIX_PATTERN}`,
    `(?:\\s*<a\\s[^>]*href=["']${CUBIC_PR_REVIEW_HREF_PATTERN}["'][^>]*>\\s*${REVIEW_IN_CUBIC_LINK_CONTENT_PATTERN}\\s*<\\/a>)?`,
    '\\s*<\\/su[bp]>',
    `(?:\\s*<a\\s[^>]*href=["']${CUBIC_PR_REVIEW_HREF_PATTERN}["'][^>]*>\\s*${REVIEW_IN_CUBIC_LINK_CONTENT_PATTERN}\\s*<\\/a>)?`,
    '\\s*',
  ].join(''),
  'gis',
);

export const GENERATED_PR_DESCRIPTION_STANDALONE_BADGE_REGEX = new RegExp(
  GENERATED_REVIEW_IN_CUBIC_BUTTON_PATTERN,
  'gis',
);

const AUTO_FIX_LOOP_HREF_PATTERN =
  '(?:https?:\\/\\/[^\\s)"\'>]*)?\\/action\\/auto-fix\\/pr\\/[^\\s)"\'>]*';
// The auto-fix loop's entry in the AI section, found by its href: the outline button beside
// "View guided diff", or the `<sub>[Turn on auto-fix](…)</sub>` line in descriptions written before it.
const autoFixLoopDescriptionEntryPattern = (hrefPattern: string) =>
  `(?:<sub>\\[[^\\]\\n]*\\]\\(${hrefPattern}\\)<\\/sub>|<a\\s[^>]*href="${hrefPattern}"[^>]*>[\\s\\S]*?<\\/a>)`;

const AUTO_FIX_LOOP_DESCRIPTION_ENTRY_REGEX = new RegExp(
  `\\s*${autoFixLoopDescriptionEntryPattern(AUTO_FIX_LOOP_HREF_PATTERN)}\\s*`,
  'gi',
);

/** The loop entry for one pull request, by the exact href the description writes for it. */
export function autoFixLoopDescriptionEntryRegex(href: string): RegExp {
  return new RegExp(autoFixLoopDescriptionEntryPattern(escapeRegExp(href)), 'g');
}

export function stripGeneratedPrDescriptionFooter(content: string): string {
  return content
    .replace(GENERATED_PR_DESCRIPTION_FOOTER_REGEX, '')
    .replace(GENERATED_PR_DESCRIPTION_STANDALONE_BADGE_REGEX, '')
    .replace(AUTO_FIX_LOOP_DESCRIPTION_ENTRY_REGEX, '');
}

export function isGeneratedPrDescriptionStandaloneBadge(
  content: string | null | undefined,
): boolean {
  const source = content?.trim();
  if (!source) {
    return false;
  }

  return (
    source.match(GENERATED_PR_DESCRIPTION_STANDALONE_BADGE_REGEX) != null &&
    stripGeneratedPrDescriptionFooter(source).trim().length === 0
  );
}

/**
 * Extracts the generated footer's commit SHA in lowercase normalized form.
 *
 * The footer regex keeps stripping behavior separate from this read-only check:
 * review context can first compare the footer commit with the PR head and only
 * discard generated text when the section is stale.
 */
export function extractGeneratedPrDescriptionFooterCommitSha(content: string): string | null {
  return (
    content.match(GENERATED_PR_DESCRIPTION_FOOTER_COMMIT_SHA_REGEX)?.[1]?.toLowerCase() ?? null
  );
}
